import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { FiUser, FiCheck, FiMail, FiChevronDown, FiClock } from 'react-icons/fi';
import ShieldCheckIcon from '../../ShieldCheckIcon';
import { toast } from '../../Toast';
import { IGlobalState } from '../../../../model/IGlobalState';
import { ContactListSummary } from '../../../../model/List';
import { listName } from './contactsData';
import ContactsModal from './ContactsModal';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const TAGS = ['None', 'Shareholder', 'Broker', 'Media', 'Adviser'];
// same choices as Send welcome email's "How did they agree?" (Contact.ConsentSource)
export const CONSENT_SOURCES = ['They asked us in person or by phone', 'They signed up on our website or at an event', 'They replied to one of our emails', 'Another way — noted on file'];

// Add a Contact (spec 219:8577). Preview only: nothing is saved.
const AddContactModal: React.FC<{ lists: ContactListSummary[]; onClose: () => void }> = ({ lists, onClose }) => {
    const stock = useSelector((state: IGlobalState) => state.currentStock);
    const user = useSelector((state: IGlobalState) => state.user);
    const company = stock?.companyName || stock?.ticker || '';
    const [first, setFirst] = useState('');
    const [last, setLast] = useState('');
    const [email, setEmail] = useState('');
    const [list, setList] = useState(lists[0]?.id ?? '');
    const [tag, setTag] = useState('Shareholder');
    const [holder, setHolder] = useState('');
    const [agreed, setAgreed] = useState(true);
    const [how, setHow] = useState(CONSENT_SOURCES[0]);
    const [welcome, setWelcome] = useState(false);
    const valid = EMAIL.test(email.trim()) && agreed;
    const recorder = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.username;

    const save = () => {
        toast.info(`Preview only: ${email.trim()} wasn’t added${welcome ? ' and no welcome email was sent' : ''}`);
        onClose();
    };

    const select = (value: string, options: { value: string; label: string }[], set: (v: string) => void, label: string) =>
        <span className="contacts-select">
            <select aria-label={label} value={value} onChange={e => set(e.target.value)}>{options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}</select>
            <FiChevronDown />
        </span>;

    return <ContactsModalShell onClose={onClose} valid={valid} onSave={save}>
        <div className="contacts-add">
            <div className="col">
                <div className="pair">
                    <label className="contacts-field"><span>First name</span><input value={first} placeholder="e.g. Margaret" onChange={e => setFirst(e.target.value)} autoFocus /></label>
                    <label className="contacts-field"><span>Last name</span><input value={last} placeholder="e.g. Chen" onChange={e => setLast(e.target.value)} /></label>
                </div>
                <label className="contacts-field"><span>Email address <b>*</b></span>
                    <span className="with-icon"><FiMail /><input type="email" value={email} placeholder="name@example.com" onChange={e => setEmail(e.target.value)} /></span>
                </label>
                <div className="pair">
                    <label className="contacts-field"><span>Add to list</span>{select(list, lists.map(l => ({ value: l.id, label: listName(l) })), setList, 'Add to list')}</label>
                    <label className="contacts-field"><span>Tags</span>{select(tag, TAGS.map(t => ({ value: t, label: t })), setTag, 'Tags')}</label>
                </div>
                <label className="contacts-field"><span>Registry holder number <em>links this contact to their shareholding</em></span>
                    <input value={holder} placeholder="e.g. I0012345" onChange={e => setHolder(e.target.value)} />
                </label>
            </div>
            <div className="col">
                <div className="contacts-permission">
                    <div className="h"><ShieldCheckIcon /> <strong>Permission to email this person</strong> <span className="req">Required</span></div>
                    <label className={`confirm${agreed ? ' on' : ''}`}>
                        <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} />
                        <span><strong>I confirm this person has agreed to receive investor emails from {company}.</strong>
                            <small>You can’t add someone who has not agreed. Adding people without permission breaches the Spam Act 2003 (Cth).</small></span>
                    </label>
                    <span className="lbl">How did they agree?</span>
                    {select(how, CONSENT_SOURCES.map(s => ({ value: s, label: s })), setHow, 'How did they agree?')}
                    <p className="rec"><FiClock /> Recorded against this contact as: {recorder} · {moment().format('D MMMM YYYY')} · added by hand</p>
                </div>
                <label className={`contacts-check-card${welcome ? ' on' : ''}`}>
                    <input type="checkbox" checked={welcome} onChange={e => setWelcome(e.target.checked)} />
                    <span><strong>Send them a welcome email now</strong><small>Uses the “Welcome Email” template</small></span>
                </label>
            </div>
        </div>
    </ContactsModalShell>;
};

const ContactsModalShell: React.FC<{ onClose: () => void; valid: boolean; onSave: () => void }> = ({ onClose, valid, onSave, children }) =>
    <ContactsModal wide icon={<FiUser />} title="Add a Contact" sub="They’ll receive announcement emails once you save" onClose={onClose}
        buttons={<button type="button" className="btn-primary" disabled={!valid} onClick={onSave}><FiCheck /> Save Contact</button>}>
        {children}
    </ContactsModal>;

export default AddContactModal;
